import React, { useCallback, useMemo, useState } from 'react';
import { Alert, Box, Button, Stack, Tab, Tabs, Typography } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useSearchParams } from 'react-router-dom';
import AdminSearchField from '../../../../components/admin/AdminSearchField';
import GameDialog from '../../../../components/games/GameDialog';
import GamesMissingPluTable from '../components/GamesMissingPluTable';
import GameReviewFilters from '../components/GameReviewFilters';
import GameReviewTable from '../components/GameReviewTable';
import MissingGamesTable from '../components/MissingGamesTable';
import GameMetadataMismatchTable from '../components/GameMetadataMismatchTable';
import { useGameMetadataMismatches } from '../hooks/useGameMetadataMismatches';
import { useGameReviewGames } from '../hooks/useGameReviewGames';
import { useGamesMissingPlu } from '../hooks/useGamesMissingPlu';
import { useMissingImageGamesLogic } from '../hooks/logic/useMissingImageGamesLogic';
import { useMissingGames } from '../hooks/useMissingGames';
import { useGamesManagement } from '../../../../hooks/useGamesManagement';
import { useStates } from '../../../../hooks/useStates';
import { buildStateMetaByCode } from '../../details/mappers/stateGamesMappers';
import { filterGameReviewGames } from '../mappers/gameReviewMappers';
import { createMissingGameDialogDefaultValues } from '../mappers/missingGameDialogMappers';
import {
  buildMissingGamesTabSearchParams,
  getMissingGamesTabFromSearchParams,
} from '../utils/missingGamesQueryParams';
import type {
  CreateGameRequest,
  Game,
  GameDialogDefaultValues,
  MissingGame,
  MissingGamesTab,
  UpdateGameRequest,
} from '../../../../types/features/games';
import {
  adminAlertSx,
  adminAlertToneSx,
  adminButtonSx,
  adminButtonToneSx,
  adminCombineSx,
  adminPageHeaderSx,
} from '../../../../styles/adminComponentStyles';

const refreshButtonSx = adminCombineSx(adminButtonSx, adminButtonToneSx.secondary, {
  height: 32,
  minHeight: 32,
  minWidth: 92,
  px: 1.5,
  py: 0,
  '& .MuiButton-startIcon': {
    ml: 0,
    mr: 0.75,
  },
  '& .MuiButton-startIcon .MuiSvgIcon-root': {
    fontSize: 18,
  },
});

const missingGamesTabs: Array<{ value: MissingGamesTab; label: string }> = [
  { value: 'missing_games', label: 'Missing Games' },
  { value: 'plu_verification_pending', label: 'PLU Verification Pending' },
  { value: 'missing_images', label: 'Missing Images' },
];

const tabsContainerSx = {
  alignItems: 'center',
  bgcolor: 'background.paper',
  borderBottom: 1,
  borderColor: 'divider',
  display: 'flex',
  flexWrap: 'wrap',
  gap: 1,
  justifyContent: 'space-between',
  mb: 2,
  px: 0,
  position: 'sticky',
  top: 0,
  zIndex: 2,
};

const tabsSx = {
  minHeight: 56,
  minWidth: 0,
  '& .MuiTab-root': {
    minHeight: 56,
    textTransform: 'none',
  },
  '& .MuiTab-root.Mui-selected': {
    fontWeight: 600,
  },
};

const reviewTableContainerSx = {
  mt: 2,
};

const searchFieldSx = {
  maxWidth: { xs: '100%', sm: 360 },
  width: '100%',
};

const MissingGamesPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { games, loading, error, reload } = useMissingGames();
  const {
    games: mismatchGames,
    loading: mismatchesLoading,
    error: mismatchesError,
    reload: reloadMismatches,
  } = useGameMetadataMismatches();
  const {
    games: gamesMissingPlu,
    loading: pluLoading,
    error: pluError,
    reload: reloadPlu,
  } = useGamesMissingPlu();
  const {
    createGame,
    createLoading,
    updateGame,
    updateLoading,
    error: gameMutationError,
    clearError: clearGameMutationError,
  } = useGamesManagement();
  const { groupedStates, states, loading: statesLoading } = useStates();
  const [search, setSearch] = useState('');
  const [selectedMissingGame, setSelectedMissingGame] = useState<MissingGame | null>(null);
  const [selectedReviewGame, setSelectedReviewGame] = useState<Game | null>(null);
  const activeTab = useMemo(() => getMissingGamesTabFromSearchParams(searchParams), [searchParams]);
  const {
    games: pluPendingGames,
    loading: pluPendingLoading,
    error: pluPendingError,
    reload: reloadPluPendingGames,
  } = useGameReviewGames('plu_verification_pending', activeTab === 'plu_verification_pending');
  const {
    games: missingImageGames,
    loading: missingImageLoading,
    error: missingImageError,
    reload: reloadMissingImageGames,
    hasSearch: hasMissingImageSearch,
    selectedState: missingImageState,
    handleStateChange: handleMissingImageStateChange,
    pagination: missingImagePagination,
  } = useMissingImageGamesLogic(search, activeTab === 'missing_images');
  const stateMetaByCode = useMemo(() => buildStateMetaByCode(groupedStates), [groupedStates]);
  const selectedMissingGameStateMeta = useMemo(() => (
    selectedMissingGame ? stateMetaByCode.get(selectedMissingGame.state) : undefined
  ), [selectedMissingGame, stateMetaByCode]);
  const dialogDefaultValues = useMemo<GameDialogDefaultValues | undefined>(() => (
    selectedMissingGame
      ? createMissingGameDialogDefaultValues(selectedMissingGame, selectedMissingGameStateMeta)
      : undefined
  ), [selectedMissingGame, selectedMissingGameStateMeta]);
  const filteredGames = useMemo(() => {
    const normalized = search.trim().toLowerCase();
    if (!normalized) {
      return games;
    }
    return games.filter((game) => (
      game.state.toLowerCase().includes(normalized)
      || game.game_id.toLowerCase().includes(normalized)
      || (game.game_name || '').toLowerCase().includes(normalized)
      || game.stores.some((store) => store.name.toLowerCase().includes(normalized))
    ));
  }, [games, search]);
  const filteredMismatchGames = useMemo(() => {
    const normalized = search.trim().toLowerCase();
    if (!normalized) {
      return mismatchGames;
    }
    return mismatchGames.filter((game) => (
      game.state.toLowerCase().includes(normalized)
      || game.game_id.toLowerCase().includes(normalized)
      || (game.game_name || '').toLowerCase().includes(normalized)
      || game.stores.some((store) => store.name.toLowerCase().includes(normalized))
    ));
  }, [mismatchGames, search]);
  const filteredPluPendingGames = useMemo(
    () => filterGameReviewGames(pluPendingGames, search),
    [pluPendingGames, search],
  );
  const filteredGamesMissingPlu = useMemo(() => {
    const normalized = search.trim().toLowerCase();
    if (!normalized) {
      return gamesMissingPlu;
    }

    return gamesMissingPlu.filter((game) => (
      [game.state, game.game_id, game.name]
        .some((value) => String(value ?? '').toLowerCase().includes(normalized))
    ));
  }, [gamesMissingPlu, search]);
  const hasSearch = search.trim().length > 0;

  const reloadAll = useCallback(
    () => Promise.all([reload(), reloadMismatches(), reloadPlu()]),
    [reload, reloadMismatches, reloadPlu],
  );
  const handleSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
  }, []);
  const handleTabChange = useCallback((_: React.SyntheticEvent, nextTab: MissingGamesTab) => {
    clearGameMutationError();
    setSearchParams(buildMissingGamesTabSearchParams(searchParams, nextTab), { replace: true });
  }, [clearGameMutationError, searchParams, setSearchParams]);
  const handleAddMissingGame = useCallback((game: MissingGame) => {
    clearGameMutationError();
    setSelectedReviewGame(null);
    setSelectedMissingGame(game);
  }, [clearGameMutationError]);
  const handleAddGameDialogClose = useCallback(() => {
    clearGameMutationError();
    setSelectedMissingGame(null);
  }, [clearGameMutationError]);
  const handleAddGameDialogSubmit = useCallback(async (data: CreateGameRequest | UpdateGameRequest): Promise<boolean> => {
    const success = await createGame(data as CreateGameRequest);

    if (success) {
      await reloadAll();
    }

    return success;
  }, [createGame, reloadAll]);
  const handleUpdateReviewGame = useCallback((game: Game) => {
    clearGameMutationError();
    setSelectedMissingGame(null);
    setSelectedReviewGame(game);
  }, [clearGameMutationError]);
  const handleUpdateGameDialogClose = useCallback(() => {
    clearGameMutationError();
    setSelectedReviewGame(null);
  }, [clearGameMutationError]);
  const handleUpdateGameDialogSubmit = useCallback(async (data: CreateGameRequest | UpdateGameRequest): Promise<boolean> => {
    if (!selectedReviewGame) {
      return false;
    }

    const success = await updateGame(selectedReviewGame.id, data as UpdateGameRequest);

    if (success) {
      if (activeTab === 'plu_verification_pending') {
        await reloadPluPendingGames();
      }

      if (activeTab === 'missing_images') {
        await reloadMissingImageGames();
      }
    }

    return success;
  }, [activeTab, reloadMissingImageGames, reloadPluPendingGames, selectedReviewGame, updateGame]);

  return (
    <Box>
      <Box sx={tabsContainerSx}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          aria-label="Missing games sections"
          variant="scrollable"
          scrollButtons="auto"
          sx={tabsSx}
        >
          {missingGamesTabs.map((tab) => (
            <Tab key={tab.value} label={tab.label} value={tab.value} />
          ))}
        </Tabs>
      </Box>

      {activeTab === 'missing_games' && (
        <Box sx={adminPageHeaderSx}>
          <Box>
            <Typography
              className="AdminPageHeader-title"
              sx={{ mb: '4px', fontSize: '24px !important' }}
            >
              Missing Games
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: '#4B5563', fontSize: 13, fontWeight: 400, lineHeight: 1.4 }}
            >
              Missing games, catalog games without a PLU, and pack metadata differences.
            </Typography>
          </Box>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
            <AdminSearchField
              placeholder="Search state, game, or store"
              value={search}
              onChange={handleSearchChange}
              sx={searchFieldSx}
            />
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              size="small"
              disabled={loading || mismatchesLoading || pluLoading}
              onClick={() => { void reloadAll(); }}
              sx={refreshButtonSx}
            >
              Refresh
            </Button>
          </Stack>
        </Box>
      )}

      {activeTab === 'missing_games' && (
        <>
          {error && (
            <Alert severity="error" sx={adminCombineSx(adminAlertSx, adminAlertToneSx.danger, { mb: 2 })}>
              {error}
            </Alert>
          )}
          {mismatchesError && (
            <Alert severity="error" sx={adminCombineSx(adminAlertSx, adminAlertToneSx.danger, { mb: 2 })}>
              {mismatchesError}
            </Alert>
          )}
          {pluError && (
            <Alert severity="error" sx={adminCombineSx(adminAlertSx, adminAlertToneSx.danger, { mb: 2 })}>
              {pluError}
            </Alert>
          )}
          {gameMutationError && (
            <Alert
              severity="error"
              sx={adminCombineSx(adminAlertSx, adminAlertToneSx.danger, { mb: 2 })}
              onClose={clearGameMutationError}
            >
              {gameMutationError}
            </Alert>
          )}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ mb: '4px', fontSize: '18px !important', lineHeight: 1.2 }}>
              Games missing PLU
            </Typography>
            <Typography
              variant="body2"
              sx={{ mb: 1.5, color: '#4B5563', fontSize: 13, fontWeight: 400, lineHeight: 1.4 }}
            >
              Catalog games without a PLU, including games with no recent store reports.
              Select a game number to open its state's games page.
            </Typography>
            <GamesMissingPluTable games={filteredGamesMissingPlu} loading={pluLoading} />
          </Box>
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ mb: '4px', fontSize: '18px !important', lineHeight: 1.2 }}>
              Games missing from the games table
            </Typography>
            <Typography
              variant="body2"
              sx={{ mb: 1.5, color: '#4B5563', fontSize: 13, fontWeight: 400, lineHeight: 1.4 }}
            >
              Add these games after verifying the suggested metadata and PLU.
            </Typography>
            <MissingGamesTable games={filteredGames} loading={loading} onAddGame={handleAddMissingGame} />
          </Box>

          <Box>
            <Typography variant="h6" sx={{ mb: '4px', fontSize: '18px !important', lineHeight: 1.2 }}>
              Pack metadata differences
            </Typography>
            <Typography
              variant="body2"
              sx={{ mb: 1.5, color: '#4B5563', fontSize: 13, fontWeight: 400, lineHeight: 1.4 }}
            >
              Compares the games table with the most common non-return pack quantity and ticket price reported by the lottery.
              Returned-ticket quantities are excluded.
            </Typography>
            <GameMetadataMismatchTable games={filteredMismatchGames} loading={mismatchesLoading} />
          </Box>
        </>
      )}

      {activeTab === 'plu_verification_pending' && (
        <Box sx={reviewTableContainerSx}>
          <GameReviewFilters
            search={search}
            searchPlaceholder="Search state, game, or PLU"
            onSearchChange={handleSearchChange}
          />
          {pluPendingError && (
            <Alert severity="error" sx={adminCombineSx(adminAlertSx, adminAlertToneSx.danger, { mb: 2 })}>
              {pluPendingError}
            </Alert>
          )}
          {gameMutationError && (
            <Alert
              severity="error"
              sx={adminCombineSx(adminAlertSx, adminAlertToneSx.danger, { mb: 2 })}
              onClose={clearGameMutationError}
            >
              {gameMutationError}
            </Alert>
          )}
          <GameReviewTable
            games={filteredPluPendingGames}
            loading={pluPendingLoading}
            ariaLabel="PLU verification pending games table"
            emptyTitle={hasSearch ? 'No matching games' : 'No PLU verification pending games'}
            emptyCopy={hasSearch ? 'Try another search.' : 'All games have completed PLU verification.'}
            onUpdate={handleUpdateReviewGame}
          />
        </Box>
      )}

      {activeTab === 'missing_images' && (
        <Box sx={reviewTableContainerSx}>
          <GameReviewFilters
            search={search}
            searchPlaceholder="Search Game ID or PLU"
            onSearchChange={handleSearchChange}
            states={states}
            statesLoading={statesLoading}
            selectedState={missingImageState}
            onStateChange={handleMissingImageStateChange}
          />
          {missingImageError && (
            <Alert severity="error" sx={adminCombineSx(adminAlertSx, adminAlertToneSx.danger, { mb: 2 })}>
              {missingImageError}
            </Alert>
          )}
          {gameMutationError && (
            <Alert
              severity="error"
              sx={adminCombineSx(adminAlertSx, adminAlertToneSx.danger, { mb: 2 })}
              onClose={clearGameMutationError}
            >
              {gameMutationError}
            </Alert>
          )}
          <GameReviewTable
            games={missingImageGames}
            loading={missingImageLoading}
            ariaLabel="missing image games table"
            emptyTitle={hasMissingImageSearch ? 'No matching games' : 'No missing images'}
            emptyCopy={hasMissingImageSearch ? 'Try another search.' : 'All games have images configured.'}
            onUpdate={handleUpdateReviewGame}
            pagination={missingImagePagination}
          />
        </Box>
      )}

      {selectedMissingGame && dialogDefaultValues && (
        <GameDialog
          open
          onClose={handleAddGameDialogClose}
          onSubmit={handleAddGameDialogSubmit}
          game={null}
          loading={createLoading}
          mode="create"
          defaultValues={dialogDefaultValues}
          states={states}
        />
      )}

      {selectedReviewGame && (
        <GameDialog
          open
          onClose={handleUpdateGameDialogClose}
          onSubmit={handleUpdateGameDialogSubmit}
          game={selectedReviewGame}
          loading={updateLoading}
          mode="update"
          states={states}
        />
      )}
    </Box>
  );
};

export default MissingGamesPage;
